import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
    Background,
    Controls,
    Edge,
    Node,
    Position,
    Handle,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { createGlobalStyle } from 'styled-components';
import {
    Box,
    Button,
    Container,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    alpha,
    useTheme
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    ArrowBack as ArrowBackIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { flowService } from '../services/flowService';
import { useNotification } from '../contexts/NotificationContext';

const GlobalStyle = createGlobalStyle`
    .react-flow__edge {
        z-index: 1 !important;
        pointer-events: all;
    }
    .react-flow__edge-path {
        stroke-width: 2;
        z-index: 1 !important;
    }
    .react-flow__node {
        z-index: 5 !important;
    }
    .react-flow__handle {
        z-index: 6;
    }
    
    /* Melhorar a visualização no modo escuro */
    .dark-mode {
        .react-flow__node {
            color: rgba(255, 255, 255, 0.9);
            
            .node-title {
                color: rgba(255, 255, 255, 0.9);
            }
            
            .node-content {
                color: rgba(255, 255, 255, 0.7);
            }
            
            .MuiTypography-root {
                color: rgba(255, 255, 255, 0.9);
                text-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);
            }
            
            .MuiPaper-root {
                background-color: rgba(30, 30, 30, 0.7);
                border-color: rgba(255, 255, 255, 0.2);
            }
        }
        
        .react-flow__background {
            background-color: #121212;
        }
    }
`;

// Componente customizado para nó de questão
const QuestionNode = ({ data, selected }) => {
    const theme = useTheme();
    
    // Função auxiliar para comparar IDs
    const isFirstQuestion = () => {
        const currentId = data.id?.toString();
        const firstQuestionId = data.flow_first_question_id?.toString();
        return currentId === firstQuestionId;
    };
    
    // Determina a cor de fundo com base no tipo de conclusão
    const getBackgroundColor = () => {
        if (!data.is_final) {
            // Aumentar o contraste no modo escuro
            return theme.palette.mode === 'dark' 
                ? alpha(theme.palette.background.paper, 0.9) // Mais opaco no modo escuro
                : theme.palette.background.paper;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.2) // Mais opaco no modo escuro
                : alpha(theme.palette.success.main, 0.1);
        } else if (data.is_success === false) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.2) // Mais opaco no modo escuro
                : alpha(theme.palette.error.main, 0.1);
        } else {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.2) // Mais opaco no modo escuro
                : alpha(theme.palette.info.main, 0.1);
        }
    };
    
    // Determina a cor da borda com base no tipo de conclusão
    const getBorderColor = () => {
        if (selected) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.8) // Borda mais visível quando selecionado no modo escuro
                : theme.palette.primary.main;
        }
        
        if (!data.is_final) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.divider, 0.7) // Borda mais visível no modo escuro
                : theme.palette.divider;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.success.main, 0.5) // Borda mais visível no modo escuro
                : alpha(theme.palette.success.main, 0.3);
        } else if (data.is_success === false) {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.error.main, 0.5) // Borda mais visível no modo escuro
                : alpha(theme.palette.error.main, 0.3);
        } else {
            return theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.5) // Borda mais visível no modo escuro
                : alpha(theme.palette.info.main, 0.3);
        }
    };
    
    // Determina a cor do texto do tipo de nó
    const getTypeColor = () => {
        if (!data.is_final) {
            return theme.palette.text.secondary;
        }
        
        if (data.is_success === true) {
            return theme.palette.success.main;
        } else if (data.is_success === false) {
            return theme.palette.error.main;
        } else {
            return theme.palette.info.main;
        }
    };
    
    // Determina o ícone do tipo de conclusão
    const getTypeIcon = () => {
        if (!data.is_final) {
            return null;
        }
        
        if (data.is_success === true) {
            return <CheckCircleIcon fontSize="small" sx={{ ml: 1 }} />;
        } else if (data.is_success === false) {
            return <CancelIcon fontSize="small" sx={{ ml: 1 }} />;
        }
        
        return null;
    };
    
    return (
        <Box sx={{ position: 'relative' }}>
            {/* Indicador de primeira questão */}
            {!data.is_final && isFirstQuestion() && (
                <Tooltip title="Primeira Questão">
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -12,
                            left: -12,
                            width: 24,
                            height: 24,
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid',
                            borderColor: theme.palette.primary.main,
                            boxShadow: theme.shadows[2],
                            zIndex: 1000
                        }}
                    >
                        <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />
                    </Box>
                </Tooltip>
            )}
            
            <Paper
                sx={{
                    p: 3,
                    minWidth: 300,
                    maxWidth: 400,
                    backgroundColor: getBackgroundColor(),
                    border: '1px solid',
                    borderColor: getBorderColor(),
                    borderRadius: 2,
                    position: 'relative',
                    boxShadow: selected 
                        ? `0 0 0 2px ${theme.palette.primary.main}`
                        : theme.shadows[2],
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                    }
                }}
            >
                {/* Handle de entrada no topo */}
                <Handle
                    type="target"
                    position={Position.Top}
                    style={{ 
                        background: theme.palette.primary.main,
                        width: 8,
                        height: 8,
                        top: -4,
                        zIndex: 1000,
                        border: `2px solid ${theme.palette.background.paper}`
                    }}
                />

                {/* Cabeçalho com tipo e referência */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                            variant="caption" 
                            component="div"
                            sx={{ 
                                color: getTypeColor(),
                                fontWeight: 500,
                                px: 1,
                                py: 0.5,
                                backgroundColor: alpha(getTypeColor(), 0.1),
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {data.is_final ? 'Conclusão' : 'Pergunta'}
                            {getTypeIcon()}
                        </Typography>
                    </Box>
                    {data.article_reference && (
                        <Typography 
                            variant="caption" 
                            component="div"
                            sx={{ 
                                color: theme.palette.text.secondary,
                                fontSize: '0.75rem',
                                px: 1,
                                py: 0.5,
                                backgroundColor: alpha(theme.palette.background.default, 0.5),
                                borderRadius: 1,
                                maxWidth: '60%'
                            }}
                        >
                            Ref: {data.article_reference}
                        </Typography>
                    )}
                </Box>

                {/* Texto principal */}
                <Typography 
                    variant="body1" 
                    sx={{ 
                        fontWeight: 500,
                        color: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.95)' 
                            : theme.palette.text.primary,
                        mb: 3,
                        lineHeight: 1.5,
                        fontSize: '1.1rem',
                        textShadow: theme.palette.mode === 'dark' 
                            ? '0px 1px 2px rgba(0, 0, 0, 0.5)' 
                            : 'none'
                    }}
                >
                    {data.text}
                </Typography>
                
                {/* Opções e edges */}
                {!data.is_final && data.options && (
                    <Box sx={{ mt: 2 }}>
                        <Box 
                            sx={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                                position: 'relative',
                                zIndex: 0
                            }}
                        >
                            {data.options.map((option, index) => (
                                <Box 
                                    key={index} 
                                    sx={{ 
                                        position: 'relative',
                                        pb: 3,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center'
                                    }}
                                >
                                    <Paper
                                        sx={{
                                            p: 1.5,
                                            width: '100%',
                                            textAlign: 'center',
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.primary.main, 0.2),
                                            borderRadius: 1.5,
                                            position: 'relative',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                                transform: 'translateY(-2px)'
                                            },
                                            zIndex: 0
                                        }}
                                    >
                                        <Typography 
                                            variant="body2"
                                            sx={{
                                                color: theme.palette.mode === 'dark' 
                                                    ? alpha(theme.palette.primary.main, 0.9)
                                                    : theme.palette.primary.main,
                                                fontWeight: 500,
                                                textShadow: theme.palette.mode === 'dark' 
                                                    ? '0px 1px 1px rgba(0, 0, 0, 0.3)' 
                                                    : 'none'
                                            }}
                                        >
                                            {option}
                                        </Typography>
                                    </Paper>
                                    
                                    {/* Handle de saída para cada opção */}
                                    <Handle
                                        type="source"
                                        position={Position.Bottom}
                                        id={index.toString()}
                                        style={{
                                            background: theme.palette.primary.main,
                                            width: 8,
                                            height: 8,
                                            bottom: 0,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            zIndex: 1000,
                                            border: `2px solid ${theme.palette.background.paper}`
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Mensagem final para conclusões */}
                {data.is_final && data.final_message && (
                    <Box sx={{ mt: 2 }}>
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                                fontStyle: 'italic',
                                backgroundColor: alpha(
                                    data.is_success === true 
                                        ? theme.palette.success.main 
                                        : data.is_success === false 
                                            ? theme.palette.error.main 
                                            : theme.palette.info.main, 
                                    0.1
                                ),
                                p: 2,
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: alpha(
                                    data.is_success === true 
                                        ? theme.palette.success.main 
                                        : data.is_success === false 
                                            ? theme.palette.error.main 
                                            : theme.palette.info.main, 
                                    0.2
                                )
                            }}
                        >
                            {data.final_message}
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

// Definindo nodeTypes fora do componente para evitar re-renderizações
const nodeTypes = {
    question: QuestionNode,
};

export const FlowPreview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showError } = useNotification();
    const theme = useTheme();
    const [flow, setFlow] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Inicializar nós e arestas
    const initializeNodesAndEdges = useCallback((questions, firstQuestionId) => {
        console.log('Inicializando com first_question_id:', firstQuestionId);

        // Recupera o mapeamento de IDs do localStorage
        const flowId = id;
        const idMappingStr = localStorage.getItem(`flow_${flowId}_id_mapping`);
        const idMapping = idMappingStr ? JSON.parse(idMappingStr) : {};
        console.log('Mapeamento de IDs recuperado:', idMapping);

        // Recupera o histórico de mapeamentos
        const idMappingHistoryStr = localStorage.getItem(`flow_${flowId}_id_mapping_history`);
        const idMappingHistory = idMappingHistoryStr ? JSON.parse(idMappingHistoryStr) : [];
        console.log('Histórico de mapeamentos:', idMappingHistory);

        // Cria um mapeamento completo combinando todos os históricos
        const completeMapping = {};
        idMappingHistory.forEach(mapping => {
            Object.entries(mapping).forEach(([originalId, newId]) => {
                completeMapping[originalId] = newId;
            });
        });

        // Adiciona o mapeamento atual
        Object.entries(idMapping).forEach(([originalId, newId]) => {
            completeMapping[originalId] = newId;
        });

        console.log('Mapeamento completo:', completeMapping);
        
        // Primeiro, criamos os nós
        const newNodes = questions.map((question) => {
            const validNextQuestionLogic = verifyNextQuestionLogic(question);
            return {
                id: question.id.toString(),
                type: 'question',
                position: { 
                    x: question.position_x || Math.random() * 500, 
                    y: question.position_y || Math.random() * 500 
                },
                data: {
                    ...question,
                    next_question_logic: validNextQuestionLogic,
                    original_id: question.original_id || question.id,
                    flow_first_question_id: firstQuestionId?.toString()
                },
            };
        });

        // Depois, criamos as edges baseadas no next_question_logic
        const newEdges = [];
        questions.forEach((question) => {
            if (question.is_final) {
                console.log(`Questão ${question.id} é final, não processando conexões`);
                return;
            }
            
            console.log(`Processando conexões para questão ${question.id} (${question.text}):`);
            const validNextQuestionLogic = verifyNextQuestionLogic(question);
            
            // Para cada opção na questão
            if (question.options && question.options.length > 0) {
                question.options.forEach((option, index) => {
                    const nextId = validNextQuestionLogic[index];
                    console.log(`Verificando opção "${option}" (${index}): nextId=${nextId}`);
                    
                    if (nextId !== null && nextId !== undefined) {
                        // Verifica se a questão destino existe usando o mapeamento
                        const mappedId = completeMapping[nextId] || nextId;
                        console.log(`ID mapeado: ${nextId} -> ${mappedId}`);
                        
                        let targetQuestion = questions.find(q => 
                            q.id === mappedId || 
                            q.id.toString() === mappedId.toString() ||
                            q.original_id === mappedId ||
                            q.id === nextId ||
                            q.id.toString() === nextId.toString() ||
                            q.original_id === nextId
                        );

                        // Se não encontrou a questão destino, tenta encontrar uma conclusão apropriada
                        if (!targetQuestion) {
                            console.log('Questão destino não encontrada, buscando conclusão apropriada...');
                            const conclusoes = questions.filter(q => q.is_final);
                            
                            if (conclusoes.length > 0) {
                                // Se a opção for "Azul", usa a primeira conclusão
                                if (option.toLowerCase() === "azul" && conclusoes.length >= 1) {
                                    targetQuestion = conclusoes[0];
                                    console.log('Usando primeira conclusão para opção "Azul"');
                                }
                                // Se a opção for "Rosa", usa a segunda conclusão (se existir)
                                else if (option.toLowerCase() === "rosa" && conclusoes.length >= 2) {
                                    targetQuestion = conclusoes[1];
                                    console.log('Usando segunda conclusão para opção "Rosa"');
                                }
                                // Caso contrário, usa a primeira conclusão disponível
                                else {
                                    targetQuestion = conclusoes[0];
                                    console.log('Usando primeira conclusão disponível como fallback');
                                }
                            }
                        }
                        
                        if (targetQuestion) {
                            console.log(`Criando conexão: ${question.id} (${option}) -> ${targetQuestion.id} (${targetQuestion.text})`);
                            const edge = {
                                id: `${question.id}-${index}-${targetQuestion.id}`,
                                source: question.id.toString(),
                                target: targetQuestion.id.toString(),
                                sourceHandle: index.toString(),
                                label: option,
                                type: 'smoothstep',
                                animated: true,
                                style: { strokeWidth: 2 }
                            };
                            newEdges.push(edge);
                        } else {
                            console.warn(`Não foi possível encontrar uma questão destino para a opção "${option}"`);
                        }
                    } else {
                        console.log(`Opção "${option}" não tem conexão definida`);
                    }
                });
            } else {
                console.warn(`Questão ${question.id} não tem opções definidas`);
            }
        });

        console.log('Nós finais:', newNodes);
        console.log('Edges finais:', newEdges);

        setNodes(newNodes);
        setEdges(newEdges);
    }, [id]);

    // Função auxiliar para verificar o next_question_logic
    const verifyNextQuestionLogic = (question) => {
        console.log('Verificando next_question_logic:', {
            id: question.id,
            text: question.text,
            next_question_logic: question.next_question_logic
        });

        if (!question.next_question_logic || typeof question.next_question_logic !== 'object') {
            console.warn('next_question_logic inválido:', question.next_question_logic);
            return {};
        }

        // Converte para objeto caso seja string JSON
        if (typeof question.next_question_logic === 'string') {
            try {
                question.next_question_logic = JSON.parse(question.next_question_logic);
            } catch (e) {
                console.error('Erro ao parsear next_question_logic:', e);
                return {};
            }
        }

        return question.next_question_logic;
    };

    // Carregar fluxo
    useEffect(() => {
        const loadFlow = async () => {
            try {
                const data = await flowService.get(Number(id));
                console.log('Fluxo carregado:', data);
                setFlow(data);
                if (data.questions) {
                    initializeNodesAndEdges(data.questions, data.first_question_id);
                }
            } catch (error) {
                console.error('Erro ao carregar fluxo:', error);
                showError('Erro ao carregar o fluxo. Por favor, tente novamente.');
            }
        };
        loadFlow();
    }, [id, showError, initializeNodesAndEdges]);

    // Atualizar nós quando o flow mudar
    useEffect(() => {
        if (flow?.questions) {
            initializeNodesAndEdges(flow.questions, flow.first_question_id);
        }
    }, [flow, initializeNodesAndEdges]);

    // Função para alternar o modo fullscreen
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            // Entrar em modo fullscreen
            const element = document.querySelector('.react-flow-container');
            if (element?.requestFullscreen) {
                element.requestFullscreen();
            } else if (element?.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element?.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            // Sair do modo fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullscreen(false);
        }
    };

    // Adicionar listener para mudanças no estado do fullscreen
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    if (!flow) return null;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4, height: 'calc(100vh - 100px)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h1">
                    {flow.name}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Button 
                        variant="outlined" 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/flows-new')}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        onClick={toggleFullscreen}
                    >
                        {isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}
                    </Button>
                </Box>
            </Box>

            <Paper 
                sx={{ 
                    height: 'calc(100vh - 200px)', 
                    position: 'relative',
                    ...(isFullscreen && {
                        height: '100vh',
                        width: '100vw',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: 9999,
                        m: 0,
                        borderRadius: 0
                    })
                }}
                className={`react-flow-container ${theme.palette.mode === 'dark' ? 'dark-mode' : ''}`}
            >
                <GlobalStyle />
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    fitView
                    minZoom={0.1}
                    maxZoom={4}
                    defaultZoom={1.5}
                    elementsSelectable={false}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    zoomOnScroll={true}
                    zoomOnPinch={true}
                    panOnScroll={true}
                    panOnDrag={true}
                    proOptions={{ hideAttribution: true }}
                    defaultEdgeOptions={{
                        style: { 
                            strokeWidth: 2, 
                            stroke: theme.palette.primary.main,
                            zIndex: 1000
                        },
                        type: 'smoothstep',
                        animated: true,
                        zIndex: 1000
                    }}
                    style={{ 
                        background: theme.palette.mode === 'dark' 
                            ? '#121212' // Fundo mais escuro no modo escuro
                            : theme.palette.background.default
                    }}
                >
                    <Background 
                        color={theme.palette.mode === 'dark' ? '#333333' : '#aaaaaa'}
                        gap={16}
                        size={1}
                        variant={theme.palette.mode === 'dark' ? 'dots' : 'dots'}
                    />
                    <Controls />
                </ReactFlow>
            </Paper>
        </Container>
    );
}; 